import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, Download } from 'lucide-react';

export default function Header() {
  const { lang, toggleLang, t, localePath } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = [
    { to: localePath('/'), label: t('nav_home') },
    { to: localePath('/support'), label: t('nav_support') },
    { to: localePath('/privacy'), label: t('nav_privacy') },
    { to: localePath('/terms'), label: t('nav_terms') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header
        data-testid="header-navigation"
        className="sticky top-0 z-50 border-b border-white/10"
        style={{
          backgroundColor: 'rgba(3,3,3,0.8)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        <div className="mx-auto max-w-[1120px] px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link to={localePath('/')} className="flex items-center gap-2 group shrink-0" data-testid="header-logo">
            <img src="/logo.png" alt="Anyelo Elite" className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg object-cover" />
            <span className="text-white font-semibold text-base sm:text-lg tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Anyelo Elite
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                  isActive(link.to)
                    ? 'text-white bg-white/10'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side: Lang toggle + CTA */}
          <div className="hidden md:flex items-center gap-3">
            <button
              data-testid="language-toggle"
              onClick={toggleLang}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 text-sm text-white/80 hover:text-white hover:border-white/20 transition-colors duration-200"
            >
              <span className="text-base leading-none">{lang === 'en' ? '🇬🇧' : '🇪🇸'}</span>
              <span data-testid="language-toggle-es" className="font-medium">{lang === 'en' ? 'EN' : 'ES'}</span>
            </button>
            <a
              href="#download"
              data-testid="header-download-cta"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
              style={{ backgroundColor: '#C8FF00', color: '#030303' }}
            >
              <Download size={16} />
              {t('nav_download')}
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 -mr-2 text-white/70 hover:text-white active:scale-95 transition-transform"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu - rendered via portal */}
      {mobileOpen && createPortal(
        <div
          className="md:hidden fixed inset-0 z-[9998] flex flex-col"
          style={{ backgroundColor: '#030303' }}
        >
          {/* Menu header */}
          <div className="flex items-center justify-between h-14 px-4 border-b border-white/10 shrink-0">
            <Link to={localePath('/')} className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
              <img src="/logo.png" alt="Anyelo Elite" className="w-7 h-7 rounded-lg object-cover" />
              <span className="text-white font-semibold text-base tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Anyelo Elite
              </span>
            </Link>
            <button
              className="p-2 -mr-2 text-white/70 hover:text-white active:scale-95 transition-transform"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X size={22} />
            </button>
          </div>

          {/* Menu content */}
          <div className="flex-1 flex flex-col px-5 pt-6 pb-8 overflow-y-auto">
            <nav className="space-y-1 flex-1">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={`block px-4 py-3.5 rounded-xl text-base font-medium transition-colors duration-200 ${
                    isActive(link.to)
                      ? 'text-white bg-white/10'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <div className="pt-6 space-y-3 border-t border-white/10 mt-auto">
              <button
                onClick={toggleLang}
                className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl border border-white/10 text-base text-white/80 active:scale-95 transition-transform"
              >
                <span className="text-lg leading-none">{lang === 'en' ? '🇬🇧' : '🇪🇸'}</span>
                <span className="font-medium">{lang === 'en' ? 'English' : 'Español'}</span>
                <span className="text-white/40 text-sm ml-auto">{lang === 'en' ? '→ ES' : '→ EN'}</span>
              </button>
              <a
                href="#download"
                onClick={() => setMobileOpen(false)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-base font-semibold active:scale-95 transition-transform"
                style={{ backgroundColor: '#C8FF00', color: '#030303' }}
              >
                <Download size={18} />
                {t('nav_download')}
              </a>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
